import { purchaserName } from '../lib/purchaser';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import {
  Button, Divider,
} from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

import PipelineBoard, { Sale, Payment, STAGES, STAGE_LABELS, money, paymentOwed, placeSale } from '../components/sales/PipelineBoard';
import SalesDialog from '../components/sales/SalesDialog';

// ─── Map backend response to frontend Sale shape ────────────
const mapApiSale = (s: any): Sale => ({
  id: s.id,
  otpDraftId: s.otpDocument?.issue?.draftId,
  lotNumber: s.lot?.lotNumber || '—',
  sectionName: s.lot?.section?.label || s.lot?.section?.name || 'Section not recorded',
  agentId: s.agentId || s.agent?.id || '',
  updatedAt: s.updatedAt,
  clientName: s.client ? purchaserName(s.client) : '—',
  clientId: s.clientId || s.client?.id || '',
  agentName: s.agent?.name || '—',
  saleAmount: s.saleAmount,
  paymentType: s.paymentType,
  stage: s.stage,
  commission: s.commission,
  downPayment: s.downPayment,
  totalPaid: s.totalPaid,
  balance: s.balance,
  installmentMonths: s.installmentMonths,
  notes: s.notes,
  contractDate: s.contractDate,
  completedDate: s.completedDate,
  createdAt: s.createdAt,
  payments: (s.payments || []).map((p: any) => ({
    id: p.id, amount: p.amount, amountPaid: p.amountPaid, penaltyAmount: p.penaltyAmount || 0,
    dueDate: p.dueDate, paidDate: p.paidDate, status: p.status, receiptNumber: p.receiptNumber,
  })),
});

const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const formatCurrency = money;

// ─── Component ──────────────────────────────────────────────

const Sales: React.FC = () => {
  const { addToast } = useToast();
  const location = useLocation();

  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);

  const [moveError, setMoveError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [movingSale, setMovingSale] = useState(false);
  const movingRef = useRef(false);
  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const data = await api.sales.board();
      setSales((data.sales || []).map(mapApiSale));
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load sales. Please retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSales(); }, [fetchSales]);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const hasShownToast = useRef(false);

  // Auto-select sale if navigated from Payments with saleId
  useEffect(() => {
    const state = location.state as { saleId?: string } | null;
    if (state?.saleId && sales.length > 0 && !hasShownToast.current) {
      const sale = sales.find(s => s.id === state.saleId);
      if (sale) {
        setSelectedSale(sale);
        addToast('info', 'Sale Details', `Viewing sale for ${sale.clientName}`);
        hasShownToast.current = true;
      }
    }
  }, [location.state, sales, addToast]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingPayment, setPayingPayment] = useState<Payment | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelingSale, setCancelingSale] = useState<Sale | null>(null);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [refundCalculation, setRefundCalculation] = useState<any>(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [refundMethod, setRefundMethod] = useState('CASH');
  const [refundReference, setRefundReference] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const refundRequestId = useRef('');

  const [pendingStageChange, setPendingStageChange] = useState<{ sale: Sale; newStage: string } | null>(null);

  const moveStage = async (saleId: string, newStage: string, beforeId: string | null = null): Promise<boolean> => {
    const sale = sales.find(s => s.id === saleId);
    if (!sale || movingRef.current || loadError) return false;
    movingRef.current = true; setMovingSale(true); setMoveError('');
    const previous = sales;
    setSales(placeSale(sales, sale, newStage, beforeId));
    try {
      await api.sales.updateStage(saleId, newStage, sale.updatedAt, beforeId);
      const data = await api.sales.board();
      const fresh = data.sales.map(mapApiSale) as Sale[];
      setSales(fresh);
      setSelectedSale(current => current ? fresh.find(s => s.id === current.id) || null : null);
      addToast('success', sale.stage === newStage ? 'Order saved' : 'Stage updated', `Lot ${sale.lotNumber} · ${STAGE_LABELS[newStage] || newStage}`);
      return true;
    } catch (err: any) {
      setSales(previous);
      setMoveError(err.message || 'Refresh the pipeline and try again.');
      addToast('error', 'Move not confirmed', err.message || 'Refresh the pipeline and try again.');
      // Reconcile possible server commits and concurrent edits before another move.
      try { const data = await api.sales.board(); const fresh = data.sales.map(mapApiSale) as Sale[]; setSales(fresh); setSelectedSale(current => current ? fresh.find(s => s.id === current.id) || null : null); }
      catch { setLoadError('The latest pipeline could not be loaded. Refresh before moving more deals.'); }
      return false;
    } finally { movingRef.current = false; setMovingSale(false); }
  };

  // ─── Record payment ───────────────────────────────────
  const recordPayment = async () => {
    if (!payingPayment || !selectedSale || recordingPayment) return;
    setRecordingPayment(true); setPaymentError('');
    try {
      await api.sales.recordPayment(payingPayment.id, { amountPaid: paymentOwed(payingPayment), expectedAmountPaid: payingPayment.amountPaid });
      const data = await api.sales.board();
      const fresh = data.sales.map(mapApiSale) as Sale[];
      setSales(fresh);
      setSelectedSale(fresh.find(s => s.id === selectedSale.id) || null);
      setShowPaymentModal(false); setPayingPayment(null);
      addToast('success', 'Payment Recorded', `${formatCurrency(paymentOwed(payingPayment))} payment recorded successfully.`);
    } catch (err: any) {
      setPaymentError(err.message || 'Payment could not be confirmed. Close and refresh the sale before retrying.');
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleCancelSale = async (sale: Sale) => {
    setCancelingSale(sale);
    setShowCancelConfirm(true);
    setRefundCalculation(null);
    setRefundMethod('CASH');
    setRefundReference('');
    setRefundNotes('');
    refundRequestId.current = window.crypto.randomUUID();
    
    // Calculate refund amount
    setLoadingRefund(true);
    try {
      const calc = await api.refunds.calculate({ saleId: sale.id });
      setRefundCalculation(calc);
    } catch (err: any) {
      console.error('Refund calculation error:', err);
      // Keep cancellation disabled until the policy calculation succeeds.
      setRefundCalculation({ error: err.message || 'Unable to calculate refund' });
    } finally {
      setLoadingRefund(false);
    }
  };

  const confirmCancelSale = async () => {
    if (!cancelingSale || loadingRefund || processingRefund || !refundCalculation || refundCalculation.error) return;
    if (cancelingSale.totalPaid > 0 && !refundNotes.trim()) { addToast('error', 'Reason required', 'Record the reason and confirm the actual refund before submitting.'); return; }
    setProcessingRefund(true);
    try {
      // Process refund if applicable
      if (refundCalculation && !refundCalculation.error && refundCalculation.calculation?.netRefundAmount >= 0 && cancelingSale.totalPaid > 0) {
        await api.refunds.process({
          requestId: refundRequestId.current,
          saleId: cancelingSale.id,
          refundAmount: refundCalculation.calculation.netRefundAmount,
          refundMethod,
          referenceNumber: refundReference,
          notes: refundNotes,
        });
        addToast('success', 'Sale Cancelled & Refund Processed', `Refund of ${formatCurrency(refundCalculation.calculation.netRefundAmount)} processed successfully.`);
      } else {
        // Just cancel without refund
        if (!(await moveStage(cancelingSale.id, 'CANCELLED'))) return;
        addToast('success', 'Sale Cancelled', 'Sale has been cancelled.');
      }
      
      setShowCancelConfirm(false);
      setCancelingSale(null);
      setSelectedSale(null);
      fetchSales(); // Refresh sales list
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to cancel sale');
    } finally {
      setProcessingRefund(false);
    }
  };

  const renderDetail = () => {
    const s = selectedSale;
    const stageIndex = s ? STAGES.indexOf(s.stage as any) : -1;
    const nextStage = stageIndex >= 0 ? STAGES[stageIndex + 1] : null;
    return <SalesDialog open={!!s} onClose={() => { if (!recordingPayment && !movingSale) setSelectedSale(null); }} title={s ? `Lot ${s.lotNumber} · Sale details` : 'Sale details'}>
      {s && <div className="sales-detail">
        <p>{s.sectionName} · {STAGE_LABELS[s.stage] || s.stage}</p>{s.otpDraftId && <p><Link to={`/sales/otp/${s.otpDraftId}/quotation`}>Issued OTP</Link> · <Link to={`/sales/otp/${s.otpDraftId}/receipts`}>Receipts and clearance</Link> · <Link to={`/sales/otp/${s.otpDraftId}/documents`}>Official documents</Link></p>}
        <dl>{[
          ['Client', s.clientName], ['Agent', s.agentName], ['Sale amount', formatCurrency(s.saleAmount)], ['Payment plan', s.paymentType === 'CASH' ? 'Cash' : `Installment${s.installmentMonths ? ` · ${s.installmentMonths} months` : ''}`],
          ['Collected', formatCurrency(s.totalPaid)], ['Outstanding balance', formatCurrency(s.balance)], ['Down payment', formatCurrency(s.downPayment)], ['Commission', formatCurrency(s.commission)],
          ['Created', formatDate(s.createdAt)], ['Contract signed', formatDate(s.contractDate)], ...(s.completedDate ? [['Completed', formatDate(s.completedDate)]] : []),
        ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
        {s.payments.length > 0 && <section><h3>Payment schedule</h3><div className="sales-schedule">{s.payments.map(p => <div key={p.id}><span>{formatDate(p.dueDate)}<small>{paymentOwed(p) > .005 ? `${formatCurrency(paymentOwed(p))} remaining` : 'Paid'}</small></span><strong>{formatCurrency(p.amount + p.penaltyAmount)}</strong>{paymentOwed(p) > .005 && !s.otpDraftId && !['CANCELLED','COMPLETED'].includes(s.stage) && <Button variant="secondary" size="sm" onClick={() => { setPaymentError(''); setPayingPayment(p); setShowPaymentModal(true); }}>Record</Button>}</div>)}</div></section>}
        {s.notes && <section><h3>Notes</h3><p style={{whiteSpace:'pre-wrap'}}>{s.notes}</p></section>}
        <p role="alert">{moveError}</p><footer>{!s.otpDraftId && !['COMPLETED','CANCELLED'].includes(s.stage) && <Button variant="ghost" disabled={movingSale} onClick={() => handleCancelSale(s)}>Cancel sale</Button>}<Button variant="secondary" onClick={() => setSelectedSale(null)}>Close</Button>{nextStage && <Button disabled={movingSale} onClick={() => { if (nextStage === 'COMPLETED') { setMoveError(''); setPendingStageChange({sale:s,newStage:nextStage}); } else void moveStage(s.id,nextStage); }}>{movingSale ? 'Saving…' : `Move to ${STAGE_LABELS[nextStage]}`}</Button>}</footer>
      </div>}
    </SalesDialog>;
  };

  return <>
    <TopBar title="Sales Pipeline" showSearch={false}/>
    <PipelineBoard sales={sales} loading={loading} error={loadError} saving={movingSale} moveError={moveError} onRetry={fetchSales} onOpen={sale => { setMoveError(''); setSelectedSale(sale); }} onMove={moveStage}/>
    {renderDetail()}
    <SalesDialog open={!!pendingStageChange} title="Complete this sale?" onClose={() => { if (!movingSale) setPendingStageChange(null); }}>
      {pendingStageChange && <div className="pipeline-move-form"><p>Lot {pendingStageChange.sale.lotNumber} · {pendingStageChange.sale.clientName}</p><p>All payments must be settled and applicable receipts cleared before completion. Issued OTP lots remain sold. Current balance: <strong>{formatCurrency(pendingStageChange.sale.balance)}</strong>.</p><p role="alert">{moveError}</p><footer><Button variant="secondary" disabled={movingSale} onClick={() => setPendingStageChange(null)}>Keep current stage</Button><Button disabled={movingSale} onClick={async () => { if (await moveStage(pendingStageChange.sale.id, pendingStageChange.newStage)) setPendingStageChange(null); }}>{movingSale ? 'Saving…' : 'Complete sale'}</Button></footer></div>}
    </SalesDialog>
    <SalesDialog open={showPaymentModal} title="Record installment payment" onClose={() => { if (!recordingPayment) setShowPaymentModal(false); }}>
      {payingPayment && <div className="pipeline-move-form"><p>Record the remaining <strong>{formatCurrency(paymentOwed(payingPayment))}</strong> for the installment due {formatDate(payingPayment.dueDate)}.</p><p>Only confirm after receiving the payment.</p><p role="alert">{paymentError}</p><footer><Button variant="secondary" disabled={recordingPayment} onClick={() => setShowPaymentModal(false)}>Cancel</Button><Button disabled={recordingPayment} onClick={recordPayment}>{recordingPayment ? 'Recording…' : 'Confirm payment'}</Button></footer></div>}
    </SalesDialog>

      {/* Cancel Sale Confirmation Modal */}
      {showCancelConfirm && cancelingSale && (
        <SalesDialog open={showCancelConfirm} title="Cancel sale & process refund" onClose={() => { if (!processingRefund) { setShowCancelConfirm(false); setCancelingSale(null); } }}>
          <div style={{ padding: '8px 0' }}>
            {/* Sale Info */}
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Icon icon="solar:danger-triangle-bold-duotone" width={32} style={{ color: '#EF4444' }} />
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>
                    Cancel This Sale?
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    This will cancel the sale and release the lot
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Client</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {cancelingSale.clientName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lot</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {cancelingSale.lotNumber}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sale Amount</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px' }}>
                    {formatCurrency(cancelingSale.saleAmount)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Paid</div>
                  <div style={{ color: '#10B981', fontWeight: 700, fontSize: '16px' }}>
                    {formatCurrency(cancelingSale.totalPaid)}
                  </div>
                </div>
              </div>
            </div>

            {/* Refund Calculation */}
            {loadingRefund && (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                <Icon icon="solar:refresh-bold-duotone" width={24} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: '8px' }}>Calculating refund...</div>
              </div>
            )}

            {!loadingRefund && refundCalculation && !refundCalculation.error && refundCalculation.calculation?.netRefundAmount > 0 && (
              <div style={{ 
                background: 'rgba(16,185,129,0.10)', 
                border: '1px solid rgba(16,185,129,0.30)',
                borderRadius: 'var(--radius-md)', 
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <Icon icon="solar:wallet-money-bold-duotone" width={28} style={{ color: '#10B981' }} />
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px' }}>
                      Refund Amount: {formatCurrency(refundCalculation.calculation.netRefundAmount)}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      Policy: {refundCalculation.policy.name}
                    </div>
                  </div>
                </div>

                {/* Refund Breakdown */}
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px', marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Breakdown
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Down Payment Refund</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(refundCalculation.calculation.downPaymentRefund)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Installments Refund</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(refundCalculation.calculation.installmentsRefund)}</span>
                    </div>
                    {refundCalculation.calculation.totalProcessingFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#EF4444' }}>
                        <span>Processing Fee</span>
                        <span style={{ fontWeight: 600 }}>-{formatCurrency(refundCalculation.calculation.totalProcessingFee)}</span>
                      </div>
                    )}
                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '15px' }}>
                      <span style={{ color: 'var(--text-primary)' }}>Net Refund</span>
                      <span style={{ color: '#10B981' }}>{formatCurrency(refundCalculation.calculation.netRefundAmount)}</span>
                    </div>
                  </div>
                </div>

                {/* Refund Method */}
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Refund Method *
                  </label>
                  <select 
                    value={refundMethod}
                    onChange={(e) => setRefundMethod(e.target.value)}
                    style={{
                      width: '100%',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="GCASH">GCash</option>
                  </select>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={refundReference}
                    onChange={(e) => setRefundReference(e.target.value)}
                    placeholder="Check #, Transaction ID, etc."
                    style={{
                      width: '100%',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Notes
                  </label>
                  <textarea
                    value={refundNotes}
                    onChange={(e) => setRefundNotes(e.target.value)}
                    placeholder="Optional notes about this refund..."
                    rows={2}
                    style={{
                      width: '100%',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      color: 'var(--input-text)',
                      fontSize: '13px',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>
            )}

            {!loadingRefund && refundCalculation?.error && (
              <div style={{ 
                background: 'rgba(239,68,68,0.10)', 
                border: '1px solid rgba(239,68,68,0.30)',
                borderRadius: 'var(--radius-md)', 
                padding: '12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: 'var(--text-secondary)'
              }}>
                <Icon icon="solar:info-circle-bold-duotone" width={16} style={{ marginRight: '8px', verticalAlign: 'middle', color: '#EF4444' }} />
                {refundCalculation.error}
              </div>
            )}

            {!loadingRefund && refundCalculation && !refundCalculation.error && refundCalculation.calculation?.netRefundAmount === 0 && (
              <div style={{ 
                background: 'var(--bg-elevated)', 
                border: '1px solid var(--border-base)',
                borderRadius: 'var(--radius-md)', 
                padding: '12px',
                marginBottom: '16px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                textAlign: 'center'
              }}>
                <Icon icon="solar:info-circle-bold-duotone" width={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                No refund applicable for this sale
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <Button variant="ghost" onClick={() => { setShowCancelConfirm(false); setCancelingSale(null); }} disabled={processingRefund}>
                Keep Sale
              </Button>
              <Button variant="danger" icon="solar:close-circle-bold-duotone" onClick={confirmCancelSale} disabled={processingRefund || loadingRefund || !!refundCalculation?.error}>
                {processingRefund ? 'Processing...' : (refundCalculation?.calculation?.netRefundAmount > 0 ? 'Cancel & Refund' : 'Cancel Sale')}
              </Button>
            </div>
          </div>
        </SalesDialog>
      )}
    </>;
};

export default Sales;
